import { DatelyObject } from "./DatelyObject";
import { Dates } from "./Dates";

import { getYearlyChangingNumbersYears, yearlyTaxFreeYears } from "../codegen";
import { Holidays, YearlyChangingNumbers, YearlyTaxFreeNumbers } from "../model";

/**
 * Helper for yearly changing sidecost percentages and holidays.
 */
export class Years {

  /**
   * Gets salary calculation parameters that are changing yearly and holidays of the year.
   * The method is designed for the end-of-year and as such it only supports 2 years:
   * the current / previous year (from Jan to Nov) OR current / next year (in approx. December).
   *
   * @param refDate - Dately object (JavaScript date, ISO string formatted date, or DayJS-object) for which the numbers are fetched.
   * @param nullIfNotSupported - Boolean indicating whether to return null if the year is not supported. By default the method throws an error if no year for the date found.
   *
   * @returns The yearly changing numbers if year supported. Error if year not supported.
   */
  public static getYearlyChangingNumbers(refDate: DatelyObject | undefined,  nullIfNotSupported = false): YearlyChangingNumbers | undefined {
    if (!refDate) {
      return undefined;
    }
    const year = Dates.getYear(refDate);
    const numbers = getYearlyChangingNumbersYears(refDate).find((x) => x.year === year);
    if (!numbers) {

      if (nullIfNotSupported) {
        return undefined;
      }
      throw new Error(`Year ${year} not supported.`);

    }
    numbers.holidays = this.getYearlyHolidays(refDate);
    return numbers;
  }

  /**
   * Gets holidays for the given year.
   *
   * @param forYear - Dately object (JavaScript date, ISO string formatted date, or DayJS-object
   * @returns The holidays for the given year.
   */
  public static getYearlyHolidays(forYear: DatelyObject): Holidays | undefined | null {
    // Implementation is moved to Dates to avoid circular dependencies.
    return Dates.getYearlyHolidays(forYear);
  }

  /**
   * Gets the yearly tax-free numbers from the tax authorities.
   * @param forYear - Dately object (JavaScript date, ISO string formatted date, or DayJS-object) for which the numbers are fetched.
   * @returns The numbers for the given year or current year. If given year is not supported, gives an error in the console (does not fail).
   */
  public static getYearlyTaxFreeNumbers(forYear: DatelyObject): YearlyTaxFreeNumbers {
    const year = Dates.getYear(forYear);
    let numbers = yearlyTaxFreeYears.find((x) => x.year == year);
    if (!numbers) {
      console.error(`getYearlyTaxFreeNumbers does not support year ${forYear || "-"}`);
      numbers = yearlyTaxFreeYears[yearlyTaxFreeYears.length - 1];
    }
    return numbers;
  }
}
